import { useEffect, useState, useRef } from 'react';
import { useForm } from 'react-hook-form';
import { yupResolver } from '@hookform/resolvers/yup';
import * as Yup from 'yup';
import { Building2, ChevronDown, Check } from 'lucide-react';
import {
  Dialog,
  DialogContent,
  DialogDescription,
  DialogFooter,
  DialogHeader,
  DialogTitle,
} from '@/components/ui/dialog';
import { Button } from '@/components/ui/button';
import { Input } from '@/components/ui/input';
import { Label } from '@/components/ui/label';
import { ErrorMessage } from '@/components/ui/error-message';
import { nameSchema, emailSchema } from '@/lib/validationSchemas';
import { useOrganizations } from '@/hooks/api/useOrganizations';

const validationSchema = Yup.object().shape({
  name: nameSchema,
  ownerEmail: emailSchema,
  organizationId: Yup.string().trim().required('Please select an organization'),
});

type FormData = Yup.InferType<typeof validationSchema>;

const defaultValues: FormData = {
  name: '',
  ownerEmail: '',
  organizationId: '',
};

interface CreateWorkspaceDialogProps {
  open: boolean;
  onOpenChange: (open: boolean) => void;
  onSubmit: (name: string, ownerEmail: string, organizationId: string) => void;
  loading?: boolean;
  /** Pre-filled org ID (when creating from org detail page) */
  organizationId?: string;
  /** Pre-filled org name for display */
  organizationName?: string;
}

export function CreateWorkspaceDialog({
  open,
  onOpenChange,
  onSubmit,
  loading,
  organizationId,
  organizationName,
}: CreateWorkspaceDialogProps) {
  const [orgSearch, setOrgSearch] = useState('');
  const [orgDropdownOpen, setOrgDropdownOpen] = useState(false);
  const [selectedOrgId, setSelectedOrgId] = useState('');
  const [selectedOrgName, setSelectedOrgName] = useState('');
  const dropdownRef = useRef<HTMLDivElement>(null);

  const {
    register,
    handleSubmit,
    setValue,
    reset,
    formState: { errors },
  } = useForm<FormData>({
    resolver: yupResolver(validationSchema),
    defaultValues: { ...defaultValues, organizationId: organizationId || '' },
  });

  // Fetch organizations with search
  const { data: orgsData, isLoading: orgsLoading } = useOrganizations({
    search: orgSearch,
    limit: 10,
    page: 1,
  });

  const organizations = orgsData?.data ?? [];

  useEffect(() => {
    if (open) {
      reset({
        name: '',
        ownerEmail: '',
        organizationId: organizationId || '',
      });
      setOrgSearch('');
      setSelectedOrgId(organizationId || '');
      setSelectedOrgName(organizationName || '');
      setOrgDropdownOpen(false);
    }
  }, [open, organizationId, organizationName, reset]);

  // Close dropdown when clicking outside
  useEffect(() => {
    const handleClickOutside = (e: MouseEvent) => {
      if (dropdownRef.current && !dropdownRef.current.contains(e.target as Node)) {
        setOrgDropdownOpen(false);
      }
    };
    document.addEventListener('mousedown', handleClickOutside);
    return () => document.removeEventListener('mousedown', handleClickOutside);
  }, []);

  const onFormSubmit = (data: FormData) => {
    onSubmit(data.name.trim(), data.ownerEmail.trim().toLowerCase(), data.organizationId.trim());
  };

  const handleOpenChange = (newOpen: boolean) => {
    if (!newOpen) {
      reset(defaultValues);
      setOrgSearch('');
      setSelectedOrgId('');
      setSelectedOrgName('');
    }
    onOpenChange(newOpen);
  };

  const handleSelectOrg = (orgId: string, orgName: string) => {
    setValue('organizationId', orgId, { shouldValidate: true });
    setSelectedOrgId(orgId);
    setSelectedOrgName(orgName);
    setOrgDropdownOpen(false);
    setOrgSearch('');
  };

  return (
    <Dialog open={open} onOpenChange={handleOpenChange}>
      <DialogContent className="sm:max-w-[425px]">
        <form onSubmit={handleSubmit(onFormSubmit)}>
          <DialogHeader>
            <DialogTitle>Create Workspace</DialogTitle>
            <DialogDescription>
              Create a new workspace. The owner must be an existing platform user.
            </DialogDescription>
          </DialogHeader>

          <div className="py-4 space-y-4">
            {organizationId ? (
              <div className="space-y-2">
                <Label>Organization</Label>
                <Input value={organizationName || organizationId} disabled className="bg-muted" />
              </div>
            ) : (
              <div className="space-y-2">
                <Label>Organization</Label>
                <div className="relative" ref={dropdownRef}>
                  <button
                    type="button"
                    onClick={() => setOrgDropdownOpen(!orgDropdownOpen)}
                    disabled={loading}
                    className={`flex h-10 w-full items-center justify-between rounded-md border px-3 py-2 text-sm ring-offset-background focus:outline-none focus:ring-2 focus:ring-ring focus:ring-offset-2 disabled:cursor-not-allowed disabled:opacity-50 ${
                      errors.organizationId ? 'border-red-500' : 'border-input'
                    }`}
                  >
                    {selectedOrgName ? (
                      <span className="flex items-center gap-2">
                        <Building2 className="h-4 w-4 text-muted-foreground" />
                        {selectedOrgName}
                      </span>
                    ) : (
                      <span className="text-muted-foreground">Select an organization...</span>
                    )}
                    <ChevronDown className="h-4 w-4 opacity-50" />
                  </button>

                  {orgDropdownOpen && (
                    <div className="absolute z-50 mt-1 w-full rounded-md border bg-popover shadow-lg">
                      <div className="p-2">
                        <Input
                          placeholder="Search organizations..."
                          value={orgSearch}
                          onChange={(e) => setOrgSearch(e.target.value)}
                          autoFocus
                          className="h-8 text-sm"
                        />
                      </div>
                      <div className="max-h-48 overflow-y-auto">
                        {orgsLoading ? (
                          <div className="px-3 py-4 text-center text-sm text-muted-foreground">
                            Loading...
                          </div>
                        ) : organizations.length === 0 ? (
                          <div className="px-3 py-4 text-center text-sm text-muted-foreground">
                            {orgSearch ? 'No organizations found' : 'No organizations available'}
                          </div>
                        ) : (
                          organizations.map((org) => (
                            <button
                              key={org._id}
                              type="button"
                              onClick={() => handleSelectOrg(org._id, org.name)}
                              className="flex w-full items-center gap-2 px-3 py-2 text-sm hover:bg-muted text-left"
                            >
                              <Check
                                className={`h-4 w-4 flex-shrink-0 ${
                                  selectedOrgId === org._id ? 'opacity-100' : 'opacity-0'
                                }`}
                              />
                              <div className="min-w-0 flex-1">
                                <div className="font-medium truncate">{org.name}</div>
                                <div className="text-xs text-muted-foreground truncate">
                                  {org.owner?.email || 'No owner'}
                                </div>
                              </div>
                            </button>
                          ))
                        )}
                      </div>
                    </div>
                  )}
                </div>
                <ErrorMessage message={errors.organizationId?.message} />
              </div>
            )}
            <div className="space-y-2">
              <Label htmlFor="workspace-name">Workspace Name</Label>
              <Input
                id="workspace-name"
                placeholder="My Workspace"
                autoFocus={!!organizationId}
                {...register('name')}
                hasError={!!errors.name}
                disabled={loading}
              />
              <ErrorMessage message={errors.name?.message} />
            </div>
            <div className="space-y-2">
              <Label htmlFor="workspace-owner-email">Owner Email</Label>
              <Input
                id="workspace-owner-email"
                type="email"
                placeholder="owner@example.com"
                {...register('ownerEmail')}
                hasError={!!errors.ownerEmail}
                disabled={loading}
              />
              <ErrorMessage message={errors.ownerEmail?.message} />
              <p className="text-xs text-muted-foreground">Must be an existing platform user</p>
            </div>
          </div>

          <DialogFooter>
            <Button
              type="button"
              variant="outline"
              onClick={() => handleOpenChange(false)}
              disabled={loading}
            >
              Cancel
            </Button>
            <Button type="submit" disabled={loading}>
              {loading ? 'Creating...' : 'Create Workspace'}
            </Button>
          </DialogFooter>
        </form>
      </DialogContent>
    </Dialog>
  );
}
